import { PERMANENT_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { HTTPStatuses, type HttpError } from '@packages/domain/constants/http';
import {
  isHttpErrorWithParsedBody,
  isHttpErrorWithStatus,
} from '@packages/domain/utils/errors/errors';
import { enableFeature } from 'actions/features';
import { newNotification } from 'actions/ui';
import {
  CANCEL_PAYMENT_FAILURE,
  CANCEL_PAYMENT_REQUEST,
  CANCEL_PAYMENT_SUCCESS,
  CHECKOUT_PLATFORM,
  CREATE_PAYMENT_FAILURE,
  CREATE_PAYMENT_REQUEST,
  CREATE_PAYMENT_SUCCESS,
  FETCH_DOCUMENTS_COLLECTIONS_FAILURE,
  FETCH_DOCUMENTS_COLLECTIONS_REQUEST,
  FETCH_DOCUMENTS_COLLECTIONS_SUCCESS,
  FETCH_DOCUMENTS_COLLECTIONS_SUCCESS_WITHOUT_RESULTS,
  MAINTENANCE_MODE,
  PROMOTIONS_OPT_IN_FIELD,
  UPLOAD_LRS_FAILURE,
  UPLOAD_LRS_REQUEST,
  UPLOAD_LRS_SIGNATURE_FILE_BAD_FORMAT,
  UPLOAD_LRS_SUCCESS,
} from 'constants/index';
import { EmbeddedRepository } from 'repositories/embedded/embedded';
import {
  getCaptchaResponse,
  getCurrentLocale,
  getFieldValue,
  getItems,
  getLinkById,
  getOrder,
  getPayment,
  getPaymentId,
  getPaymentToken,
  getRecipientId,
  getStudentID,
  isFetching,
  paymentIsFromAgents,
  shouldHideUserManagement,
} from 'selectors';
import { logger } from 'services/logger/logger';
import { lrs } from 'services/lrs/lrs';
import type { PayexService } from 'services/payex';
import type { RepeatPaymentPayload } from 'services/payex/payments/types';
import type { PayexThunkAction } from 'store/configureStore';
import { callbackPayment } from 'utils/callbackPayment/callbackPayment';
import { cookies } from 'utils/cookies/cookies';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { getI18nInstance } from 'utils/translations';
import type { FieldValue } from '../../../models';

const SIGNATURE_FILE_ERROR = 'invalid_signature';
const ENGLISH = 'en';

const getCreatePaymentCallback = () => {
  const onPaymentCreated = EmbeddedRepository.getProp('onPaymentCreated');

  const callback = payment => onPaymentCreated(callbackPayment(payment));

  return onPaymentCreated && callback;
};

export const createPayment =
  (): PayexThunkAction<Promise<unknown>> =>
  async (dispatch, getState, { payments, promotions, userLanguage }) => {
    const state = getState();
    const isCreatingPayment = isFetching(state, 'createPayment');

    if (isCreatingPayment) {
      return;
    }

    dispatch({ type: CREATE_PAYMENT_REQUEST });

    const locale = getCurrentLocale(state);
    const senderEmail = getFieldValue(state, 'sender_email');
    if (locale !== ENGLISH) {
      userLanguage.update(senderEmail, locale);
    }

    const promotionsOptIn = getFieldValue(state, PROMOTIONS_OPT_IN_FIELD);
    if (promotionsOptIn) {
      promotions.subscribe(senderEmail);
    }

    const { id, token } = getOrder(state);
    const captchaResponse = getCaptchaResponse(state);
    const callback = getCreatePaymentCallback();
    const hideUserManagement = shouldHideUserManagement(state);
    const recipient = getRecipientId(state);
    const platform = CHECKOUT_PLATFORM;

    const params = {
      orderId: id,
      token,
      locale,
      callback,
      hideUserManagement,
      captchaResponse,
      platform,
      recipient,
    };

    try {
      const normalizedPayment = await payments.create(params);

      return dispatch({
        type: CREATE_PAYMENT_SUCCESS,
        payload: normalizedPayment,
      });
    } catch (error) {
      if (error.status === 503) {
        return dispatch(enableFeature(MAINTENANCE_MODE));
      }

      const { parsedBody } = error;

      if (error.status === 422 && platform === CHECKOUT_PLATFORM) {
        logger.log({
          description: 'EMBEDDED 2.0 INTEGRATION - PAYMENT CREATION FAILED',
          parameters: {
            recipient,
            erroredBody: parsedBody,
          },
        });
      }

      dispatch({
        type: CREATE_PAYMENT_FAILURE,
        payload: parsedBody,
      });

      throw error;
    }
  };

export const cancelPayment =
  (cancellationLink = null) =>
  async (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isCancellingPayment = isFetching(state, 'paymentManagement');

    if (isCancellingPayment) {
      return;
    }

    dispatch({ type: CANCEL_PAYMENT_REQUEST });

    let cancellationMethod = 'POST';

    if (!cancellationLink) {
      const { href, method } = getLinkById(state, 'cancellation') || {};
      cancellationLink = href;
      cancellationMethod = method;
    }

    if (!cancellationLink) {
      return;
    }

    let cancellationReason;
    if (paymentIsFromAgents(state)) {
      cancellationReason = 'agents';
    }

    try {
      const response = await apiV3.orders.cancelPayment({
        url: cancellationLink,
        method: cancellationMethod,
        cancellationReason,
      });

      await logger.log({
        description: 'payment cancelled by user',
        parameters: {
          payment: getPayment(state),
        },
      });

      dispatch({
        type: CANCEL_PAYMENT_SUCCESS,
        payload: response,
      });
      return response;
    } catch (error) {
      const { status } = error;

      if (
        status !== HTTPStatuses.BAD_REQUEST &&
        status !== HTTPStatuses.NOT_ALLOWED
      ) {
        error.message = `${CANCEL_PAYMENT_FAILURE} ${error.message}`;
        errorNotifier.notifyWithFingerprint(error, [
          CANCEL_PAYMENT_FAILURE,
          status,
        ]);
      }

      await logger.log({
        description: 'payment cancellation failure',
        parameters: {
          payment: getPayment(state),
          error: error.message,
        },
      });

      return dispatch({ type: CANCEL_PAYMENT_FAILURE });
    }
  };

export const uploadLRS = data => async (dispatch, getState) => {
  const state = getState();
  const paymentId = getPaymentId(state);
  const paymentToken = getPaymentToken(state);
  const studentId = getStudentID(state);
  const locale = getCurrentLocale(state);
  const i18n = getI18nInstance(locale);

  const handleError = (
    error: HttpError,
    senderEmail?: FieldValue,
    partner?: string,
  ) => {
    let messageKey = `notifications.${UPLOAD_LRS_FAILURE.toLowerCase()}`;
    if (isHttpErrorWithParsedBody(error)) {
      error.message = `${UPLOAD_LRS_FAILURE} ${error.message}`;
      errorNotifier.notifyWithFingerprint(
        error,
        [UPLOAD_LRS_FAILURE, `${error.status || ''}`],
        {
          ...data,
          paymentId,
          ...(senderEmail && { senderEmail }),
          ...(partner && { partner }),
        },
      );
      const { parsedBody } = error;
      if (
        parsedBody?.error &&
        typeof parsedBody?.error === 'string' &&
        SIGNATURE_FILE_ERROR === parsedBody.error.toLowerCase()
      ) {
        dispatch({ type: UPLOAD_LRS_SIGNATURE_FILE_BAD_FORMAT });
        messageKey = `${messageKey}.${SIGNATURE_FILE_ERROR}`;
      }
    }
    const message = i18n.t(messageKey) as string;
    dispatch(newNotification({ message, type: 'error' }));
    return dispatch({ type: UPLOAD_LRS_FAILURE });
  };

  dispatch({ type: UPLOAD_LRS_REQUEST });

  try {
    await lrs.upload({
      bankAccountNumber: data.bankAccountNumber,
      paymentId,
      paymentToken,
      signatureImage: data.signatureImage,
      studentFullName: data.studentFullName,
      studentID: studentId as string,
      transactions: data.transactions ?? [],
    });

    return dispatch({ type: UPLOAD_LRS_SUCCESS });
  } catch (error) {
    return handleError(error as HttpError);
  }
};

export const repeatPayment =
  () =>
  async (_dispatch, getState, { payex }: { payex: PayexService }) => {
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || 'no_fingerprint';
    const state = getState();
    const payment = getPayment(state);
    const items = getItems(state);
    const locale = getCurrentLocale(state);
    const itemsPayload = items
      .filter(field => !field.apiReadOnly)
      .map(item => ({
        id: item.id,
        amount: Number(getFieldValue(state, item.id)),
      }));

    const payload: RepeatPaymentPayload = {
      id: payment.id,
      paymentToken: payment.token,
      items: itemsPayload,
      fingerprint,
      locale,
    };
    const repeatedPayment = await payex.payments.repeatPayment(payload);

    return repeatedPayment;
  };

export const fetchDocumentsCollections =
  (): PayexThunkAction =>
  async (dispatch, getState, { payex }) => {
    const state = getState();
    const isFetchingDocumentsCollections = isFetching(
      state,
      'documentsCollections',
    );
    const paymentId = getPaymentId(state);

    if (!paymentId || isFetchingDocumentsCollections) {
      return;
    }

    dispatch({ type: FETCH_DOCUMENTS_COLLECTIONS_REQUEST });

    const { token } = getPayment(state);
    const locale = getCurrentLocale(state);
    try {
      const response = await payex.payments.fetchDocumentsCollections({
        id: paymentId,
        token,
        locale,
      });

      return dispatch({
        type: FETCH_DOCUMENTS_COLLECTIONS_SUCCESS,
        payload: response,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status } = error as HttpError;

        if (status === HTTPStatuses.NOT_FOUND) {
          return dispatch({
            type: FETCH_DOCUMENTS_COLLECTIONS_SUCCESS_WITHOUT_RESULTS,
          });
        }
      }

      errorNotifier.notify(error);

      return dispatch({ type: FETCH_DOCUMENTS_COLLECTIONS_FAILURE });
    }
  };
